import React from 'react';
import {
  StyleProp,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
  ViewStyle,
} from 'react-native';

import { useActiveSettlementMethodQuery } from 'apollo/queries/autogenerate/hooks';
import {
  AutoDebitSettlementMethodStatus,
  BillStatement,
  BillStatementStatusV2,
} from 'apollo/queries/autogenerate/schemas';
import { getBillDueDate } from 'shared/modules/bill/utils/get-bill-due-date';
import Badge, {
  Props as BadgeProps,
} from 'shared/modules/common/components/atoms/Badge';
import Button from 'shared/modules/common/components/atoms/Button';
import Icon from 'shared/modules/common/components/atoms/Icon';
import generateID from 'shared/modules/common/utils/generate-id';
import { getNumberOfDaysToNow } from 'shared/utils/date';
import { formatToCurrency } from 'shared/utils/japanese';

const getBillLabels = (
  isAutoDebit: boolean,
  isOverdue: boolean,
  dueDate: string
) => {
  return {
    // Overdue bills can only be paid through conbini or bank transfer.
    // After 120 days past due date, payment can only be done through bank transfer.
    method:
      isAutoDebit && !isOverdue
        ? '口座振替'
        : getNumberOfDaysToNow(dueDate) > 120
        ? '銀行振込'
        : 'コンビニ払い/銀行振込',
    billedAmount: isAutoDebit ? 'お引き落とし金額' : 'お支払い金額の確認',
    dueDate: isAutoDebit ? 'お引き落とし予定日' : 'お支払い期間',
    lateFee: '遅延手数料',
    pay: isAutoDebit && !isOverdue ? '口座振替について' : 'お支払い',
    transferFund:
      isAutoDebit && !isOverdue ? '口座振替について' : '支払い方法を選ぶ',
  };
};

const getBillPaidDate = (date: string) => {
  const paidDate = new Date(date);

  return `${paidDate.getMonth() + 1}月${paidDate.getDate()}日`;
};

const getBillBadge = (
  billStatus: BillStatementStatusV2,
  billAmount: number,
  id = ''
) => {
  if (billStatus === BillStatementStatusV2.OUTSTANDING && id !== '') {
    return null;
  }

  let text = '';
  let variant: BadgeProps['variant'];

  if (!billAmount) {
    text = 'ご利用なし';
  } else {
    if (billStatus === BillStatementStatusV2.OVERDUE) {
      text = 'お支払い待ち';
      variant = 'danger';
    }

    if (billStatus === BillStatementStatusV2.PAID) {
      text = 'お支払い済み';
      variant = 'success';
    }
  }

  return <Badge title={text} variant={variant} id={id} />;
};

type Props = {
  bill: BillStatement;
  /**
   * Invoked when the amount text is pressed
   */
  onDetailPress?: (bill: BillStatement) => void;
  /**
   * Invoked when the "Pay" button is pressed
   */
  onPayButtonPress?: (bill: BillStatement) => void;
  /**
   * Disables the "Pay" button because the older bill must be paid first
   */
  isOldestUnpaidBill?: boolean;
  /**
   * Is this BillCard used to display Highlighted Bills?
   */
  isHighlightedBill?: boolean;
  style?: StyleProp<ViewStyle>;
  id?: string;
  showTitle?: boolean;
  showPayButton?: boolean;
  showPaymentMethod: boolean;
  onPaymentMethodPress?: (billid: string) => void;
};

const BillCard = (props: Props) => {
  const bill = props.bill;
  const billingDate = new Date(bill.period.end);
  const billingCycle = bill.billingCycle;
  const nonAutoDebitDueDate = new Date(bill.dueAt);

  const { data: activeSettlementMethod } = useActiveSettlementMethodQuery({
    skip: billingCycle === '',
    variables: {
      billingCycle,
    },
  });

  // Bill states
  const isAutoDebit =
    activeSettlementMethod?.activeSettlementMethod.settlementMethods.autoDebit
      .status === AutoDebitSettlementMethodStatus.ACTIVE;
  const isPaid = bill.statusV2 === BillStatementStatusV2.PAID;
  const isOverdue = bill.statusV2 === BillStatementStatusV2.OVERDUE;

  // If the bill is not yet PAID, use this. This amount changes for any user activities
  // e.g partially pay the bill or make refunds.
  // This automatically includes the lateFeeAmount if the bill becomes OVERDUE.
  const outstandingAmount = bill.outstandingAmount;

  // Unpaid amount is immutable. This number is created during the bill generation.
  // Do not use this if the bill is still unpaid, because users can choose to partially pay or make refunds.
  // Use this only when bill id PAID.
  const unpaidAmount = bill.unpaidAmount;

  // Use only alongside the unpaidAmount when bill is fully paid.
  // So PAID bills should show unpaidAmount + lateFeeAmount
  const lateFeeAmount = bill.lateFeeAmount;

  const labels = getBillLabels(isAutoDebit, isOverdue, bill.dueAt);

  // If using auto-debit, use the autodebit dates from Contentful
  // else use the bill's dueAt
  const dueDate = getBillDueDate(isAutoDebit, bill, activeSettlementMethod);

  // Disallow pressing pressable elements under certain conditions
  const isPressDisabled =
    !unpaidAmount || (!isPaid && !props.isOldestUnpaidBill);

  const showTitle = props.showTitle === undefined || props.showTitle === true;
  const showPayButton =
    props.showPayButton === undefined || props.showPayButton === true;
  const showPaymentMethod = props.showPaymentMethod === true;

  // RENDER PLACEHOLDER BILL IF BILL ID IS UNKNOWN
  if (!bill.id) {
    return (
      <View
        style={[style.root, props.style]}
        /** Generate HTML and Test IDs  */
        {...generateID(props.id)}
      >
        <Text
          style={[style.titlePlaceholder, style.textBold]}
        >{`${billingDate.getFullYear()}年${
          billingDate.getMonth() + 1
        }月分のご請求`}</Text>
        <View style={style.labels}>
          <Text style={[style.labelText, style.textSecondary]}>
            お支払い待ちのご請求はありません。
            {`\n${billingDate.getMonth() + 1}`}
            月分のご請求は
            <Text
              style={[style.labelText, style.textSecondary, style.textBold]}
            >{`${nonAutoDebitDueDate.getMonth() + 1}月１日`}</Text>
            に確定します。
          </Text>
        </View>
        <View style={style.separator} />
        <View style={style.labels}>
          <Text style={style.labelText}>{labels.dueDate}</Text>
          <Text
            style={[
              style.labelText,
              style.textBold,
              isOverdue && style.textDanger,
            ]}
            /** Generate HTML and Test IDs  */
            {...generateID(props.id && `${props.id}-due-date`)}
          >
            {dueDate}
          </Text>
        </View>
        <View style={style.labels}>
          <Text style={style.labelText}>お支払い方法</Text>
          {props.onPaymentMethodPress !== undefined ? (
            <TouchableOpacity
              onPress={() =>
                //For Upcoming bill since the placeholder bill has bill id as empty string '', we are passing `upcoming` string as id so that we can display details of upcoming bill on Account page. If we pass empty string as bill id then the parameter received on Account page will be undefined. In that case we will show details of oldestOutstandingBill (case when user goes to Account page directly from left navigation)
                props.onPaymentMethodPress &&
                props.onPaymentMethodPress('upcoming')
              }
            >
              <Text
                style={[style.labelText, style.textBold]}
                /** Generate HTML and Test IDs  */
                {...generateID(props.id && `${props.id}-payment-method`)}
              >
                {labels.method}
              </Text>
            </TouchableOpacity>
          ) : (
            <Text
              style={[style.labelText, style.textBold]}
              /** Generate HTML and Test IDs  */
              {...generateID(props.id && `${props.id}-payment-method`)}
            >
              {labels.method}
            </Text>
          )}
        </View>
      </View>
    );
  }

  // RENDER NORMAL BILLS
  return (
    <View
      style={[style.root, props.style]}
      /** Generate HTML and Test IDs  */
      {...generateID(props.id)}
    >
      {showTitle && (
        <Text
          style={[style.title, !props.isHighlightedBill && style.titleCentered]}
        >{`${billingDate.getFullYear()}年${
          billingDate.getMonth() + 1
        }月分のご請求`}</Text>
      )}
      <View style={style.labels}>
        <Text style={style.labelText}>{labels.billedAmount}</Text>
      </View>
      <TouchableOpacity
        disabled={isPressDisabled || !props.isHighlightedBill}
        onPress={() => props.onDetailPress?.(props.bill)}
        style={style.amountWrapper}
      >
        <Text
          style={style.amountText}
          /** Generate HTML and Test IDs  */
          {...generateID(props.id && `${props.id}-amount`)}
        >
          {formatToCurrency(
            isPaid ? unpaidAmount + lateFeeAmount : outstandingAmount
          )}
        </Text>
        <View style={style.amountStatusWrapper}>
          {getBillBadge(
            bill.statusV2,
            bill.amount,
            props.id && `${props.id}-status-badge`
          )}
        </View>
        {!isPressDisabled && props.isHighlightedBill && (
          <Icon name="caret-right" size="24" tintColor="#A1AAB0" />
        )}
      </TouchableOpacity>
      {bill.amount < 1 && (
        <Text style={style.labelText}>お支払い待ちのご請求はありません。</Text>
      )}
      {bill.amount >= 1 && (
        <>
          <View style={style.separator} />
          {lateFeeAmount > 0 && !props.isHighlightedBill && (
            <View style={style.labels}>
              <Text style={style.labelText}>{labels.lateFee}</Text>
              <Text
                style={[style.labelText, style.textBold]}
                /** Generate HTML and Test IDs  */
                {...generateID(props.id && `${props.id}-late-fee`)}
              >
                {formatToCurrency(lateFeeAmount)}
              </Text>
            </View>
          )}
          {!isPaid && (
            <View style={style.labels}>
              <Text style={style.labelText}>{labels.dueDate}</Text>
              <Text
                style={[
                  style.labelText,
                  style.textBold,
                  isOverdue && style.textDanger,
                ]}
                /** Generate HTML and Test IDs  */
                {...generateID(props.id && `${props.id}-due-date`)}
              >
                {dueDate}
              </Text>
            </View>
          )}
          {isPaid && (
            <View style={style.labels}>
              <Text style={style.labelText}>お支払い日</Text>
              <Text
                style={[style.labelText, style.textBold]}
                /** Generate HTML and Test IDs  */
                {...generateID(props.id && `${props.id}-payment-date`)}
              >
                {getBillPaidDate(bill.paidAt)}
              </Text>
            </View>
          )}
          {showPaymentMethod && (
            <View style={style.labels}>
              <Text style={style.labelText}>お支払い方法</Text>

              {props.onPaymentMethodPress ? (
                <TouchableOpacity
                  onPress={() =>
                    props.onPaymentMethodPress &&
                    props.onPaymentMethodPress(bill.id)
                  }
                >
                  <Text
                    style={[style.labelText, style.textBold]}
                    /** Generate HTML and Test IDs  */
                    {...generateID(props.id && `${props.id}-payment-method`)}
                  >
                    {labels.method}
                  </Text>
                </TouchableOpacity>
              ) : (
                <Text
                  style={[style.labelText, style.textBold]}
                  /** Generate HTML and Test IDs  */
                  {...generateID(props.id && `${props.id}-payment-method`)}
                >
                  {labels.method}
                </Text>
              )}
            </View>
          )}
          {isAutoDebit && !isOverdue && !props.isHighlightedBill && (
            <Text
              style={[
                style.labelText,
                !isPaid && style.marginBottom,
                style.textCenter,
              ]}
            >
              ※ お引き落としが反映されるまでに3～5営業日程度かかります。
            </Text>
          )}
          {!isPaid && unpaidAmount && showPayButton && (
            <>
              <View style={style.payButtonWrapper}>
                <Button
                  title={
                    props.isHighlightedBill ? labels.pay : labels.transferFund
                  }
                  onPress={() => props.onPayButtonPress?.(props.bill)}
                  variant={isOverdue ? 'danger' : 'primary'}
                  disabled={isPressDisabled}
                  titleStyle={style.buttonTitle}
                  style={style.payButton}
                  id={props.id && `${props.id}-pay-button`}
                />
              </View>
              {isPressDisabled && (
                <Text
                  style={[style.labelText, style.textDanger, style.payText]}
                  /** Generate HTML and Test IDs  */
                  {...generateID(props.id && `${props.id}-pay-message`)}
                >
                  先に発行されたご請求をお支払いください。
                </Text>
              )}
            </>
          )}
        </>
      )}
    </View>
  );
};

const style = StyleSheet.create({
  root: {
    backgroundColor: 'white',
    padding: 24,
    justifyContent: 'center',
  },
  title: {
    fontSize: 12,
    marginBottom: 16,
  },
  titlePlaceholder: {
    marginBottom: 8,
  },
  // If props.isHighlightedBill is false
  titleCentered: {
    fontSize: 16,
    textAlign: 'center',
    fontWeight: 'bold',
  },
  amountWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 16,
  },
  amountText: {
    fontSize: 28,
    fontWeight: 'bold',
    marginRight: 16,
  },
  amountStatusWrapper: {
    flexDirection: 'row',
    flexGrow: 1,
  },
  separator: {
    height: StyleSheet.hairlineWidth,
    backgroundColor: '#CFD4D8',
    marginBottom: 16,
  },
  labels: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
  },
  labelText: {
    fontSize: 12,
  },
  textBold: {
    fontWeight: 'bold',
  },
  textDanger: {
    color: '#FF2751',
  },
  textSecondary: {
    color: '#586065',
  },
  textCenter: {
    textAlign: 'center',
  },
  payButtonWrapper: {
    alignItems: 'center',
  },
  payButton: {
    maxWidth: 296,
    height: 40,
  },
  payText: {
    textAlign: 'center',
    marginTop: 16,
  },
  marginBottom: {
    marginBottom: 16,
  },
  buttonTitle: {
    fontSize: 12,
  },
});

export default BillCard;
